import type { Recipe } from '@awwyeah/shared';

/**
 * Code-computed card art for a recipe with no photo (WS-20).
 *
 * Owner correction, 2026-08-04: no model call for this, ever — "I don't want to use tokens
 * to make a placeholder that's just an icon with a gradient." So this is a pure function:
 * a keyword→emoji lookup matched against the title and ingredient names, plus a gradient
 * deterministically chosen by hashing the recipe id. Nothing is stored (no `placeholderArt`
 * schema field) — it's recomputed on every render, which means it can never go stale and a
 * keyword-table improvement instantly reaches every existing recipe.
 */

export const PLACEHOLDER_GRADIENT_KEYS = [
  'citrus',
  'clay',
  'sage',
  'plum',
  'sky',
  'ember',
  'dusk',
  'sand',
] as const;
export type PlaceholderGradientKey = (typeof PLACEHOLDER_GRADIENT_KEYS)[number];

/**
 * Each key is a Tailwind gradient utility string built entirely from the app's semantic
 * color tokens (`bg-surface`/`text-fg-2`/`--accent`/`--m-*`/…, `apps/web/src/theme/
 * tokens.css`) — never a raw hex value. That's what "theme-safe" means here: every theme
 * (light, dark, bubblegum, the four wild ones) recolors these for free, because the
 * gradient stops ARE the theme's own tokens, just blended. A future theme needs zero
 * changes here to render a sensible placeholder.
 *
 * Browser-verified fix (WS-20), two rounds:
 *
 * 1. The first pass used the `--m-*-tint` tokens, which are deliberately very pale in the
 *    *light* theme (they're a subtle background wash for member-colored chips elsewhere) —
 *    pale-on-pale-off-white made six of the eight keys read as blank white in light mode.
 * 2. The fix for that (`from-accent/40` etc — solid colors at reduced opacity) turned out
 *    to silently generate NO CSS at all: this Tailwind version's JIT scanner does not emit
 *    `from-*`/`via-*` gradient-stop utilities when an opacity modifier (`/NN`) is combined
 *    with a `var()`-based theme color (confirmed directly against the Tailwind CLI in
 *    isolation — bare `from-member-ezry` generates fine, `from-member-ezry/35` generates
 *    nothing, on this exact color config). So: **no opacity modifiers on gradient stops,
 *    ever** — every key below uses bare solid/tint tokens only, which both generates
 *    reliably and (re-verified in the browser) reads as a real, visible gradient in light,
 *    dark and cosmos.
 */
const GRADIENT_CLASSES: Record<PlaceholderGradientKey, string> = {
  citrus: 'bg-gradient-to-br from-accent via-highlight to-surface',
  clay: 'bg-gradient-to-br from-member-ezry-tint via-highlight to-member-ezry',
  sage: 'bg-gradient-to-br from-member-dan-tint via-raised to-member-dan',
  plum: 'bg-gradient-to-br from-member-kora-tint via-raised to-member-kora',
  sky: 'bg-gradient-to-br from-member-faye-tint via-raised to-member-faye',
  ember: 'bg-gradient-to-br from-danger via-highlight to-surface',
  dusk: 'bg-gradient-to-br from-member-kora via-member-ezry to-surface',
  sand: 'bg-gradient-to-br from-line-strong via-raised to-surface',
};

/** Tailwind class string for a gradient key — the only thing a component needs. */
export function gradientClassName(key: PlaceholderGradientKey): string {
  return GRADIENT_CLASSES[key];
}

export interface PlaceholderArt {
  emoji: string;
  bg: PlaceholderGradientKey;
}

/**
 * Keyword → emoji rules, most-specific first. `first-match-wins` against the haystack
 * (title + ingredient names, lowercased) — a rule further down never overrides one above
 * it, which is why "chicken fajita" hits 🌮 (fajita is listed before chicken) rather than
 * 🍗, matching how a family would actually describe the dish.
 */
const EMOJI_RULES: Array<{ test: RegExp; emoji: string }> = [
  // Specific dishes/cuisines before their generic protein/ingredient.
  { test: /\b(taco|fajita|burrito|quesadilla|enchilada)s?\b/, emoji: '🌮' },
  { test: /\b(pizza|flatbread)s?\b/, emoji: '🍕' },
  { test: /\b(curry|tikka|masala|korma)\b/, emoji: '🍛' },
  { test: /\b(stir[\s-]?fry|fried rice|lo mein|chow mein)\b/, emoji: '🍚' },
  { test: /\b(sushi|maki|nigiri)\b/, emoji: '🍣' },
  { test: /\b(ramen|pho|udon)\b/, emoji: '🍜' },
  { test: /\b(soup|stew|chili|chowder|bisque)\b/, emoji: '🍲' },
  { test: /\b(cake|cupcake)s?\b/, emoji: '🍰' },
  { test: /\b(pasta|orzo|spaghetti|penne|linguine|fettuccine|lasagna|noodles?)\b/, emoji: '🍝' },
  { test: /\b(salmon|cod|tilapia|halibut|trout|tuna|fish)\b/, emoji: '🐟' },
  { test: /\b(shrimp|prawn|scallop)s?\b/, emoji: '🍤' },
  { test: /\b(bread|toast|baguette|focaccia|sourdough)\b/, emoji: '🍞' },
  { test: /\b(salad|slaw)\b/, emoji: '🥗' },
  { test: /\b(cookie|biscotti)s?\b/, emoji: '🍪' },
  { test: /\b(sauce|marinade|dressing|vinaigrette)\b/, emoji: '🥫' },
  { test: /\b(pancake|crepe|waffle)s?\b/, emoji: '🥞' },
  { test: /\b(rice|risotto|paella|pilaf)\b/, emoji: '🍚' },
  { test: /\b(burger|cheeseburger)s?\b/, emoji: '🍔' },
  { test: /\b(sandwich|sub|hoagie|panini)\b/, emoji: '🥪' },
  { test: /\b(smoothie|milkshake|shake)s?\b/, emoji: '🥤' },
  { test: /\b(pie|tart)s?\b/, emoji: '🥧' },
  { test: /\b(ice cream|gelato|sorbet)\b/, emoji: '🍨' },
  { test: /\bbrownies?\b/, emoji: '🍫' },
  { test: /\bmuffins?\b/, emoji: '🧁' },
  { test: /\bdonuts?\b/, emoji: '🍩' },
  { test: /\bdumplings?\b/, emoji: '🥟' },
  { test: /\btamales?\b/, emoji: '🫔' },
  { test: /\bmeatballs?\b/, emoji: '🍡' },
  { test: /\bmac and cheese\b/, emoji: '🧀' },
  { test: /\bwings?\b/, emoji: '🍗' },
  { test: /\bribs?\b/, emoji: '🍖' },
  { test: /\bkabobs?|skewers?\b/, emoji: '🍢' },
  { test: /\bgranola|oatmeal|porridge\b/, emoji: '🥣' },
  { test: /\bbagels?\b/, emoji: '🥯' },
  { test: /\bfries\b/, emoji: '🍟' },
  { test: /\bpopcorn\b/, emoji: '🍿' },
  { test: /\bhoney\b/, emoji: '🍯' },

  // Breakfast/eggs before "egg" appears buried in an ingredient list of something else.
  { test: /\b(breakfast|brunch)\b/, emoji: '🥞' },
  { test: /\begg(s)?\b/, emoji: '🍳' },
  { test: /\bbacon\b/, emoji: '🥓' },

  // Base proteins.
  { test: /\b(chicken|poultry|turkey|hen)\b/, emoji: '🍗' },
  { test: /\b(beef|steak|brisket|roast)\b/, emoji: '🥩' },
  { test: /\bpork\b/, emoji: '🥓' },
  { test: /\blamb\b/, emoji: '🍖' },
  { test: /\btofu\b/, emoji: '🧈' },

  // Common produce/staples, generic fallback territory.
  { test: /\bpotato(es)?\b/, emoji: '🥔' },
  { test: /\bavocado\b/, emoji: '🥑' },
  { test: /\bcorn\b/, emoji: '🌽' },
  { test: /\bmushroom(s)?\b/, emoji: '🍄' },
  { test: /\bcheese\b/, emoji: '🧀' },
  { test: /\bdrink|cocktail|punch\b/, emoji: '🥤' },
  { test: /\bcasserole\b/, emoji: '🍲' },
  { test: /\bveg(etable|gie)s?\b/, emoji: '🥦' },
  { test: /\bfruit\b/, emoji: '🍓' },
  { test: /\bchocolate\b/, emoji: '🍫' },
];

const FALLBACK_EMOJI = '🍽️';

/** Lowercased haystack the keyword rules match against: title + every ingredient's bare
 * name (falling back to its raw text when `name` wasn't split out). */
function haystackFor(recipe: Pick<Recipe, 'title' | 'ingredients'>): string {
  const words = [recipe.title, ...recipe.ingredients.map((i) => i.name ?? i.text)];
  return words.join(' ').toLowerCase();
}

export function emojiFor(recipe: Pick<Recipe, 'title' | 'ingredients'>): string {
  const haystack = haystackFor(recipe);
  for (const rule of EMOJI_RULES) {
    if (rule.test.test(haystack)) return rule.emoji;
  }
  return FALLBACK_EMOJI;
}

/** Deterministic small-int hash — same recipe id always picks the same gradient, so a
 * card doesn't visually shuffle between renders. Not cryptographic; doesn't need to be. */
function hashToIndex(id: string, mod: number): number {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  }
  return hash % mod;
}

export function placeholderArtFor(
  recipe: Pick<Recipe, 'id' | 'title' | 'ingredients'>,
): PlaceholderArt {
  const index = hashToIndex(recipe.id, PLACEHOLDER_GRADIENT_KEYS.length);
  return {
    emoji: emojiFor(recipe),
    bg: PLACEHOLDER_GRADIENT_KEYS[index] as PlaceholderGradientKey,
  };
}
